import axios from '../utils/axios';
import { AUTH_STORAGE_KEY, DEFAULT_API_BASE_URL } from '../constants';
import { parseJwtPayload } from '../utils/authTokens';
import { getErrorStatus, isSsoTemporarilyUnavailable } from '../utils/requestErrors';

export type PrintingCaller = { id: string; issuer: string; audience: string };
declare module 'axios' {
  interface AxiosRequestConfig { ridPrintingCaller?: PrintingCaller }
}

export type PendingPrintIntent = {
  version: 1;
  caller: PrintingCaller;
  organizationId: string;
  requestId: string;
  sessionId: string;
  payloadJson: string;
  completedPrintRunId?: number;
  rejection?: { message: string };
};

export const PRINT_INTENT_CHANGED = 'rid-print-intent-changed';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

export class PrintingPendingError extends Error {
  constructor(message = 'Your print request is waiting for confirmation. Resume it before starting another print.') {
    super(message);
    this.name = 'PrintingPendingError';
  }
}

export class PrintingRejectedError extends Error {
  constructor(message: string) { super(message); this.name = 'PrintingRejectedError'; }
}

export function currentPrintingCaller(): PrintingCaller {
  const claims = parseJwtPayload(window.localStorage.getItem(AUTH_STORAGE_KEY) || '');
  const id = String(claims?.Id ?? '');
  if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(Number(id))
    || typeof claims?.iss !== 'string' || !claims.iss
    || !(typeof claims?.aud === 'string' || Array.isArray(claims?.aud))) {
    throw new PrintingPendingError('Sign in with the original account to continue printing.');
  }
  return { id, issuer: claims.iss, audience: JSON.stringify(claims.aud) };
}

export function assertPrintingCaller(expected: PrintingCaller): void {
  if (JSON.stringify(currentPrintingCaller()) !== JSON.stringify(expected)) {
    throw new PrintingPendingError('The signed-in account changed. Sign in with the original account to resume this print.');
  }
}

function storageKey(caller: PrintingCaller, organizationId: string): string {
  return `rid.print-intent.v1:${encodeURIComponent(DEFAULT_API_BASE_URL)}:${encodeURIComponent(caller.issuer)}:${encodeURIComponent(caller.audience)}:${caller.id}:${organizationId}`;
}

function validUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID.test(value) && value !== EMPTY_UUID;
}

function readIntent(key: string, caller: PrintingCaller, organizationId: string): PendingPrintIntent | null {
  const stored = window.localStorage.getItem(key);
  if (!stored) return null;
  try {
    const value = JSON.parse(stored) as PendingPrintIntent;
    const payload = JSON.parse(value?.payloadJson);
    if (value?.version !== 1 || JSON.stringify(value?.caller) !== JSON.stringify(caller)
      || value?.organizationId !== organizationId || !validUuid(value?.requestId)
      || !validUuid(value?.sessionId) || !payload || typeof payload !== 'object' || Array.isArray(payload)
      || String(payload.organizationId) !== organizationId
      || (value.rejection !== undefined && (!value.rejection || typeof value.rejection.message !== 'string'
        || !value.rejection.message.trim() || value.rejection.message.length > 4096 || value.completedPrintRunId !== undefined))
      || (value.completedPrintRunId !== undefined && (!Number.isSafeInteger(value.completedPrintRunId) || value.completedPrintRunId <= 0))) {
      throw new Error('Invalid intent');
    }
    return value;
  } catch {
    throw new PrintingPendingError('The saved print request cannot be read. Retain it and contact support before starting another print.');
  }
}

function saveIntent(key: string, value: PendingPrintIntent): void {
  const json = JSON.stringify(value);
  try {
    window.localStorage.setItem(key, json);
    if (window.localStorage.getItem(key) !== json) throw new Error('Storage did not retain intent');
  } catch {
    throw new PrintingPendingError('The browser could not save your print request. Free browser storage before trying again.');
  }
  window.dispatchEvent(new Event(PRINT_INTENT_CHANGED));
}

export function getPendingPrintIntent(organizationId: number | string): PendingPrintIntent | null {
  const caller = currentPrintingCaller();
  const org = String(organizationId);
  return readIntent(storageKey(caller, org), caller, org);
}

async function locked<T>(key: string, operation: () => Promise<T>): Promise<T> {
  if (!navigator.locks?.request) {
    throw new PrintingPendingError('This browser cannot safely retain a print request. Use a browser that supports Web Locks.');
  }
  return navigator.locks.request(key, operation);
}

function header(headers: unknown, name: string): unknown {
  const values = headers as { get?: (name: string) => unknown; [key: string]: unknown } | undefined;
  return values?.get ? values.get(name) : values?.[name.toLowerCase()] ?? values?.[name];
}

function retryDelay(headers: unknown): number {
  const value = Number(header(headers, 'Retry-After'));
  // The printing protocol returns seconds. Bound both malformed and extreme hints.
  return Math.max(1, Math.min(30, Number.isFinite(value) ? value : 1)) * 1000;
}

export type PrintRequestOptions = {
  signal?: AbortSignal;
  onWaiting?: () => void;
  // RID-3178: the print is saved, but RetailID is still sending its label count to Metrc in the background.
  onMcaCountDelayed?: () => void;
};

export const MCA_COUNT_HEADER = 'X-Rid-Mca-Count';

async function submitSaved(key: string, intent: PendingPrintIntent, options: PrintRequestOptions): Promise<unknown> {
  if (intent.rejection) throw new PrintingRejectedError(intent.rejection.message);
  // One bounded recovery cycle. Exhaustion retains the original intent for an explicit resume.
  for (let attempt = 0; attempt < 8; attempt += 1) {
    assertPrintingCaller(intent.caller);
    if (options.signal?.aborted) throw new PrintingPendingError();
    let delay = 1000;
    try {
      const response = await axios.post('/printrun/add', JSON.parse(intent.payloadJson), {
        ridPrintingCaller: intent.caller,
        signal: options.signal,
        timeout: 20000,
        headers: {
          'X-Rid-Printing-Protocol': 'durable-v1',
          'X-Rid-Printing-Request-Id': intent.requestId,
          'X-Rid-Printing-Session-Id': intent.sessionId,
        },
      });
      // Missing headers are also uncertain: an intermediary must not change the binding.
      if (header(response.headers, 'X-Rid-Printing-Request-Id') !== intent.requestId
        || header(response.headers, 'X-Rid-Printing-Session-Id') !== intent.sessionId
        || header(response.headers, 'X-Rid-Printing-Protocol') !== 'durable-v1') {
        throw new PrintingPendingError('The server response could not confirm your original print request. Resume it before starting another print.');
      }
      if (response.status === 202) {
        if (response.data?.status !== 'pending' || response.data?.requestId !== intent.requestId) throw new PrintingPendingError();
        delay = retryDelay(response.headers);
      } else {
        const id = Number(response.data?.id ?? response.data?.Id);
        if (response.status !== 200 || !Number.isSafeInteger(id) || id <= 0 || response.data?.error === true) {
          throw new PrintingPendingError('The server did not confirm a saved print run. Resume this request before starting another print.');
        }
        saveIntent(key, { ...intent, completedPrintRunId: id });
        if (options.signal?.aborted) throw new PrintingPendingError();
        if (String(header(response.headers, MCA_COUNT_HEADER) ?? '').toLowerCase() === 'delayed') options.onMcaCountDelayed?.();
        return response.data;
      }
    } catch (error) {
      const status = getErrorStatus(error);
      const response = error && typeof error === 'object'
        ? (error as { response?: { status?: number; headers?: unknown; data?: { status?: string; requestId?: string; sessionId?: string; message?: unknown } } }).response : undefined;
      const failure = response?.data;
      if (response?.status === 400 && failure?.status === 'rejected'
        && failure?.requestId === intent.requestId && failure?.sessionId === intent.sessionId
        && header(response.headers, 'X-Rid-Printing-Request-Id') === intent.requestId
        && header(response.headers, 'X-Rid-Printing-Session-Id') === intent.sessionId
        && header(response.headers, 'X-Rid-Printing-Protocol') === 'durable-v1'
        && typeof failure.message === 'string' && failure.message.trim() && failure.message.length <= 4096) {
        assertPrintingCaller(intent.caller);
        saveIntent(key, { ...intent, rejection: { message: failure.message } });
        if (options.signal?.aborted) throw new PrintingPendingError();
        throw new PrintingRejectedError(failure.message);
      }
      if (error instanceof PrintingPendingError || (status !== undefined && ![502, 503, 504].includes(status))) throw error;
      // RetailID's own "Metrc sign-in temporarily unavailable" answer is final, not an uncertain gateway failure.
      // Stop retrying and keep the intent, so the user sees why and can resume later.
      if (isSsoTemporarilyUnavailable(error)) throw error;
      // A network/gateway failure can follow a committed admission. Reuse every original input.
    }
    if (options.signal?.aborted) throw new PrintingPendingError();
    options.onWaiting?.();
    await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
  }
  throw new PrintingPendingError();
}

export async function addDurablePrintRun(payload: unknown, options: PrintRequestOptions = {}): Promise<unknown> {
  // Serialize before admission so caller mutations and undefined fields cannot alter a retry.
  const payloadJson = JSON.stringify(payload);
  const frozen = JSON.parse(payloadJson);
  const org = String(frozen?.organizationId ?? '');
  if (!frozen || Array.isArray(frozen) || !/^[1-9]\d*$/.test(org)) throw new Error('Organization is required to print.');
  const caller = currentPrintingCaller();
  const key = storageKey(caller, org);
  return locked(key, async () => {
    assertPrintingCaller(caller);
    const existing = readIntent(key, caller, org);
    if (existing) {
      if (existing.payloadJson !== payloadJson) throw new PrintingPendingError();
      return submitSaved(key, existing, options);
    }
    const protocol = await axios.get('/printrun/printing-protocol', {
      params: { organizationId: frozen.organizationId }, ridPrintingCaller: caller, signal: options.signal,
    });
    assertPrintingCaller(caller);
    if (options.signal?.aborted) throw new Error('Print request cancelled before sending.');
    if (protocol.data?.protocol === 'legacy' && protocol.data?.sessionId === null) {
      // Only a first intent can select legacy. A retained durable request never falls back.
      const result = await axios.post('/printrun/add', frozen, { ridPrintingCaller: caller, signal: options.signal });
      if (options.signal?.aborted) throw new Error('Printing was interrupted. Check Print Runs before starting again.');
      return result.data;
    }
    if (protocol.data?.protocol !== 'durable-v1' || !validUuid(protocol.data?.sessionId)) {
      throw new Error('The server did not provide a supported printing protocol.');
    }
    const intent: PendingPrintIntent = { version: 1, caller, organizationId: org,
      requestId: crypto.randomUUID(), sessionId: protocol.data.sessionId, payloadJson };
    saveIntent(key, intent);
    return submitSaved(key, intent, options);
  });
}

export async function resumePendingPrintRun(organizationId: number | string, options: PrintRequestOptions = {}): Promise<unknown> {
  const caller = currentPrintingCaller();
  const org = String(organizationId);
  const key = storageKey(caller, org);
  return locked(key, async () => {
    const intent = readIntent(key, caller, org);
    if (!intent) throw new Error('There is no waiting print request.');
    return submitSaved(key, intent, options);
  });
}

export async function acknowledgeCompletedPrintRun(organizationId: number | string, printRunId: number | string): Promise<void> {
  const caller = currentPrintingCaller();
  const org = String(organizationId);
  const key = storageKey(caller, org);
  await locked(key, async () => {
    const intent = readIntent(key, caller, org);
    if (!intent) return;
    if (intent.completedPrintRunId !== Number(printRunId)) throw new PrintingPendingError();
    window.localStorage.removeItem(key);
    window.dispatchEvent(new Event(PRINT_INTENT_CHANGED));
  });
}

// Acknowledgment is available only after the original immutable rejection was verified.
export async function acknowledgeRejectedPrintRun(organizationId: number | string, requestId: string): Promise<void> {
  const caller = currentPrintingCaller();
  const org = String(organizationId);
  const key = storageKey(caller, org);
  await locked(key, async () => {
    assertPrintingCaller(caller);
    const intent = readIntent(key, caller, org);
    if (!intent) return;
    if (!intent.rejection || intent.requestId !== requestId || intent.completedPrintRunId !== undefined) throw new PrintingPendingError();
    window.localStorage.removeItem(key);
    window.dispatchEvent(new Event(PRINT_INTENT_CHANGED));
  });
}
